/**
 * Tutorial action cues — the Phaser half of the "this needs your touch"
 * language: a subtle breathing scale pulse (≈4%, one gentle cycle every
 * ~1.3s) applied to the REAL rack tile the current lesson requires. The
 * React half animates the gold spotlight ring; together they make the
 * required object feel active without a hand/finger indicator.
 *
 * Attached ONLY in scripted (tutorial) presentation — real matches never
 * receive cues. Driven by React via the `tutorial:cue` CustomEvent
 * ({ tileId: number | null }); null clears. The pulse never touches input
 * eligibility, legality, or any gameplay state — it is a visual tween on
 * the sprite's scale, fully reverted on change, clear, and detach.
 *
 * prefers-reduced-motion: no tween at all — the React ring's static
 * treatment carries the cue alone.
 */

export const CUE_EVENT = 'tutorial:cue';

export interface CueDetail { tileId: number | null }

/** The slice of a Phaser scene's tween manager the cue module needs. */
export interface CueTweens {
  add(config: object): unknown;
  killTweensOf(target: object): void;
}

export interface CueSprite {
  active: boolean;
  setScale(scale: number): unknown;
}

export interface CueSources {
  tweens: CueTweens;
  getHandTileSprite(tileId: number): CueSprite | null;
  /** Injectable for tests; defaults to the real media query. */
  prefersReducedMotion?: () => boolean;
}

const PULSE_SCALE = 1.04;
const HALF_CYCLE_MS = 650; // full breath ≈ 1.3s — inside the 1.1–1.5s spec

export function attachTutorialActionCues(sources: CueSources): () => void {
  const reduced = sources.prefersReducedMotion
    ?? (() => typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  let current: CueSprite | null = null;

  const clear = () => {
    if (current) {
      sources.tweens.killTweensOf(current);
      if (current.active) current.setScale(1);
      current = null;
    }
  };

  const onCue = (e: Event) => {
    const tileId = (e as CustomEvent<CueDetail>).detail?.tileId ?? null;
    const next = tileId !== null ? sources.getHandTileSprite(tileId) : null;
    if (next === current) return;
    clear();
    if (!next || !next.active) return;
    current = next;
    if (reduced()) return; // static — the React ring carries the cue
    sources.tweens.add({
      targets:  next,
      scaleX:   PULSE_SCALE,
      scaleY:   PULSE_SCALE,
      duration: HALF_CYCLE_MS,
      ease:     'Sine.easeInOut',
      yoyo:     true,
      repeat:   -1,
    });
  };

  window.addEventListener(CUE_EVENT, onCue);
  return () => {
    window.removeEventListener(CUE_EVENT, onCue);
    clear();
  };
}
